import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {HELP_TOOLTIP_STYLE} from 'processes/ProcessConstants';
import ConfigureTaskFinderButton from 'processes/settings/ConfigureTaskFinderButton';
import FormGroup from 'ui-library/bs/FormGroup';
import {GRAY_DARK} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {SettingsLabel, SettingsPopover} from 'ui-library/SettingsField';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';

type ProcessOutputSchema = ProcessTaskSchemaGraphQL['outputSchema'];

interface InnerConfigureTaskFinderSectionProps {
  taskFinderLookupConfig: LookupConfigSpec | null;
  outputSchema: ProcessOutputSchema | null;
  taskSchema: ProcessTaskSchemaGraphQL | null;
  isBeingCreated: boolean;
  isArchived: boolean;
  error?: string;
  onChange: (newLookup: LookupConfigSpec | null) => void;
}

export interface ConfigureTaskFinderSectionProps extends InnerConfigureTaskFinderSectionProps {
  isLoading: boolean;
}

export const LOOKUP_HELP_TEXT =
  'Configure a lookup to automatically associate each output with a corresponding task field.';

function InnerConfigureTaskFinderSection(props: InnerConfigureTaskFinderSectionProps): JSX.Element {
  if (props.isBeingCreated) {
    return (
      <span className={css(styles.descriptionText)}>
        Configure the lookup after the task schema is created.
      </span>
    );
  }
  return (
    <>
      <ConfigureTaskFinderButton
        {...props}
        taskSchema={props.taskSchema!}
        outputSchema={props.outputSchema!}
      />
      <ErrorMessage error={props.error ?? null} />
    </>
  );
}

export default function ConfigureTaskFinderSection(props: ConfigureTaskFinderSectionProps): JSX.Element {
  return (
    <div className={css(styles.taskFinderWrapper)}>
      <FormGroup className={classNames('u-no-spacing-bottom', css(styles.descriptionContents))}>
        <SettingsLabel
          label="Output → task lookup"
          name="Output → task lookup"
          helpIcon={
            <SettingsPopover
              renderContent={() => <div className={css(styles.helpTooltip)}>{LOOKUP_HELP_TEXT}</div>}
            />
          }
        />
        {props.isLoading ? <HorizontalThrobber /> : <InnerConfigureTaskFinderSection {...props} />}
      </FormGroup>
    </div>
  );
}

const styles = StyleSheet.create({
  taskFinderWrapper: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '32px',
    marginBottom: '32px',
  },
  descriptionContents: {
    flex: 0.5,
  },
  descriptionText: {
    color: GRAY_DARK,
    fontSize: '13px',
    lineHeight: '16px',
  },
  helpTooltip: HELP_TOOLTIP_STYLE,
});
